import React, { useEffect, useState, useRef } from "react";

export const RightPanelTables = ({ editor, isShow, isOpen }) => {
  const [tables, setTables] = useState([]);

  useEffect(() => {
    if (editor?.dom && isShow === "tables" && isOpen) {
      const allTables = editor.dom.select("table-wrap");
      const allXrefs = editor.dom.select("xref");

      if (allTables && allTables.length > 0) {
        const newTables = [];
        allTables.forEach((item, index) => {
          const id = item.getAttribute("id") || `table-gen-${index}`;
          const label = item.querySelector("label")?.textContent || id || "Table";
          const title = item.querySelector("title")?.textContent || item.querySelector("caption")?.textContent || item.querySelector("title1")?.textContent || "";
          
          // Get the actual inner table HTML
          const tableElement = item.querySelector("table");
          const tableHtml = tableElement ? tableElement.outerHTML : "";

          const xrefCount = Array.from(allXrefs).filter((xref) => {
            const refType = xref.getAttribute("ref-type") || "";
            if (refType !== "table") return false;
            const href = xref.getAttribute("href") || "";
            const rid = xref.getAttribute("rid") || "";
            
            const cleanId = id.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            const cleanHref = href.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            const cleanRid = rid.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            
            const idNum = id.replace(/\D/g, "");
            const hrefNum = href.replace(/\D/g, "");
            const ridNum = rid.replace(/\D/g, "");
            
            const matchesStr = cleanId === cleanRid || cleanId === cleanHref;
            const matchesNum = !!(idNum && (idNum === hrefNum || idNum === ridNum));
            
            return matchesStr || matchesNum;
          }).length;

          newTables.push({
            id,
            label,
            title,
            tableHtml,
            count: xrefCount,
          });
        });
        setTables(newTables);
      } else {
        setTables([]);
      }
    }
  }, [isShow, isOpen, editor]);

  const selectTableInEditor = (id) => {
    if (!editor || !id) return;
    const dom = editor.dom;
    const queryEle = dom.select(`table-wrap[id="${id}"]`)[0];
    if (queryEle) {
      editor.selection.select(queryEle);
      queryEle.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const xrefIndicesRef = useRef({});

  const handleClick = (id) => {
    const editorBody = editor?.dom;
    if (!editorBody) return;

    const allXrefs = editorBody.select("xref");
    const xrefs = Array.from(allXrefs).filter((xref) => {
      const refType = xref.getAttribute("ref-type") || "";
      if (refType !== "table") return false;
      const href = xref.getAttribute("href") || "";
      const rid = xref.getAttribute("rid") || "";
      
      const cleanId = id.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      const cleanHref = href.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      const cleanRid = rid.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
      
      const idNum = id.replace(/\D/g, "");
      const hrefNum = href.replace(/\D/g, "");
      const ridNum = rid.replace(/\D/g, "");
      
      const matchesStr = cleanId === cleanRid || cleanId === cleanHref;
      const matchesNum = !!(idNum && (idNum === hrefNum || idNum === ridNum));
      
      return matchesStr || matchesNum;
    });

    if (!xrefs.length) return;

    if (xrefIndicesRef.current[id] === undefined) {
      xrefIndicesRef.current[id] = 0;
    }

    const currentIndex = xrefIndicesRef.current[id] % xrefs.length;
    const targetElement = xrefs[currentIndex];

    if (targetElement) {
      editor.selection.select(targetElement);
      targetElement.focus();
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    xrefIndicesRef.current[id] = (xrefIndicesRef.current[id] + 1) % xrefs.length;
  };

  if (isShow !== "tables") return null;

  return (
    <div className="w-full flex flex-col gap-4">
      {/* Table Style Overrides for Right Panel Preview */}
      <style dangerouslySetInnerHTML={{__html: `
        .table-preview-container table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 6px !important;
          font-size: 11px !important;
          background: #FFFFFF !important;
        }
        .table-preview-container th, .table-preview-container td {
          border: 1px solid #E3E0D8 !important;
          padding: 4px 6px !important;
          text-align: left !important;
        }
        .table-preview-container th {
          background-color: #F4F3EF !important;
          font-weight: 600 !important;
        }
      `}} />

      {tables.length > 0 ? (
        <div className="flex flex-col gap-3">
          {tables.map((item, i) => (
            <div
              key={i}
              className="relative w-full flex flex-col cursor-pointer bg-white rounded-md hover:bg-panelrefbg border border-grey py-2 px-[6px] hover:border-panelref"
            >
              <div
                onClick={() => selectTableInEditor(item.id)}
                className="flex flex-col w-full gap-[6px] pb-6"
              >
                <span className="font-semibold text-sm text-[#4F46E5]">{item.label}</span>
                {item.title && (
                  <p className="text-[13px] text-gray-800 font-medium">{item.title}</p>
                )}
                {item.tableHtml && (
                  <div 
                    className="table-preview-container w-full overflow-x-auto bg-[#FCFBF9] border border-[#E3E0D8] rounded p-1.5"
                    dangerouslySetInnerHTML={{ __html: item.tableHtml }}
                  />
                )}
              </div>
              <div className="flex justify-end z-9 absolute bottom-[8px] right-[8px]">
                <span
                  onClick={() => handleClick(item.id)}
                  className="text-[12px] text-green hover:text-red font-medium"
                >
                  cited{`(${item.count})`}
                </span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-gray-400 py-8 text-sm">No tables found in document</div>
      )}
    </div>
  );
};
